import { Box, Tooltip } from '@mui/material';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

import { PaloThumb } from './palo-thumb';

const c = tokens.color;

/** The picture of an article as the API hands it out: one address per format. */
export type PaloPreviewImage = Partial<Record<string, string>>;

export interface PaloPositionImageProps {
    image?: PaloPreviewImage | null;
    name: string;
    /** Under the name in the preview, e.g. "Art.-Nr. 10113075". */
    caption?: ReactNode;
}

/**
 * The picture of a position, small enough to keep the row height, with a
 * larger preview on hover so nobody has to open the article (LST-S18, #2341).
 * Only the small formats are loaded, never the original file.
 */
export const PaloPositionImage = ({ image, name, caption }: PaloPositionImageProps): ReactElement => {
    const thumb = image?.['70x70'];
    // A file that cannot be loaded gets the placeholder and no preview: an empty
    // frame on hover would only say the same thing larger.
    const [failed, setFailed] = useState(false);

    useEffect(() => setFailed(false), [thumb]);

    const frame = (
        <PaloThumb src={thumb} alt={name} width={26} height={26} sx={{ mx: 'auto' }} onFail={() => setFailed(true)} />
    );

    if (!thumb || failed) {
        return frame;
    }

    return (
        <Tooltip
            placement="right"
            enterDelay={150}
            componentsProps={{
                tooltip: {
                    sx: {
                        backgroundColor: c.surface,
                        color: c.ink,
                        border: `1px solid ${c.line}`,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, .12)',
                        padding: '8px',
                        maxWidth: 'none',
                    },
                },
            }}
            title={
                <Box sx={{ width: 150 }}>
                    <Box
                        component="img"
                        src={image?.['200x200'] ?? thumb}
                        alt={name}
                        sx={{ width: 150, height: 150, objectFit: 'contain', display: 'block' }}
                    />
                    <Box sx={{ fontSize: '12px', mt: '6px', lineHeight: 1.3 }}>{name}</Box>
                    {caption && <Box sx={{ fontSize: '11px', color: c.ink2, mt: '2px' }}>{caption}</Box>}
                </Box>
            }
        >
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>{frame}</Box>
        </Tooltip>
    );
};
